import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment.base';
import { SiteService } from './site.service';
import { easyFormatter } from '../easyFormatter';
import {
  AppRelease,
  AppReleaseSendRequest,
  AppReleaseSummary,
  AppVersionInfo,
} from '../models/app-release';

export interface Notification {
  recipientIds?: number[] | null;
  /** Every enabled user except the sender. Wins over recipientIds. */
  allUsers?: boolean;
  notification: string;
  /** Show as a popup on delivery too; stored as kind = 'alert'. */
  popup?: boolean;
  /** 'md' when the body is Markdown (the Notification page always sends this). */
  format?: 'md';
  /** 'fullscreen' — open the popup/slideshow filling the browser window. */
  display?: 'fullscreen' | null;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    private siteService: SiteService,
    private http: HttpClient
  ) {}

  send(notification: Notification): Observable<any> {
    const url = environment.api.host + environment.api.paths.api.notification.send;

    this.siteService.addSubscriptionLog(
      this,
      'notification.service.ts->send->this.http.post<any>(url, notification)'
    );

    return this.http.post<any>(url, notification).pipe(
      finalize(() =>
        this.siteService.setSubscriptionLogFinalised(
          'notification.service.ts->send->this.http.post<any>(url, notification)'
        )
      )
    );
  }

  // ── App releases ─────────────────────────────────────────────────────────

  /** Latest recorded release, no body. Cheap; polled on load + tab-return. */
  getAppVersion(): Observable<AppVersionInfo> {
    const url = environment.api.host + environment.api.paths.api.notification.appVersion;

    return this.http.get<{ data: AppVersionInfo }>(url).pipe(map((res) => res.data));
  }

  getReleases(): Observable<AppReleaseSummary[]> {
    const url = environment.api.host + environment.api.paths.api.notification.releases;

    return this.http.get<{ data: AppReleaseSummary[] }>(url).pipe(map((res) => res.data));
  }

  getRelease(id: string): Observable<AppRelease> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.notification.release,
      { id }
    );

    return this.http.get<{ data: AppRelease }>(url).pipe(map((res) => res.data));
  }

  sendRelease(
    release: AppReleaseSendRequest
  ): Observable<{ release: AppRelease; recipientCount: number }> {
    const url = environment.api.host + environment.api.paths.api.notification.sendRelease;

    return this.http
      .post<{ data: { release: AppRelease; recipientCount: number } }>(url, release)
      .pipe(map((res) => res.data));
  }

  // ── Inbox ────────────────────────────────────────────────────────────────

  getAll(): Observable<any> {
    const url = environment.api.host + environment.api.paths.api.notification.getAll;

    return this.http.get<any>(url).pipe(
      map((res) => {
        return res;
      })
    );
  }

  readAll(): Observable<any> {
    const url = environment.api.host + environment.api.paths.api.notification.readAll;

    return this.http.put<any>(url, null).pipe(
      map((res) => {
        return res;
      })
    );
  }

  archiveAll(): Observable<any> {
    const url = environment.api.host + environment.api.paths.api.notification.archiveAll;

    return this.http.put<any>(url, null).pipe(
      map((res) => {
        return res;
      })
    );
  }

  read(id: number): Observable<any> {
    const url = easyFormatter(environment.api.host + environment.api.paths.api.notification.read, {
      id: id.toString(),
    });

    return this.http.put<any>(url, null).pipe(
      map((res) => {
        return res;
      })
    );
  }

  archive(id: number): Observable<any> {
    const url = easyFormatter(
      environment.api.host + environment.api.paths.api.notification.archive,
      {
        id: id.toString(),
      }
    );

    return this.http.put<any>(url, null).pipe(
      map((res) => {
        return res;
      })
    );
  }
}
